import { QueryData, api_fetch } from "@/api"
import { GlobalFilterState, analytics_date_kind, useCustomerRefs } from "@/global_filter"
import { TableState } from "@/deduction_table/table_state"
import { addDays, format, parseISO } from "date-fns"
import { useEffect, useMemo, useState } from "preact/hooks"
import { useLocation } from "wouter-preact"
import useSWR from "swr"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"
import {
  ALL_KEY,
  ALL_LABEL,
  aggregate_to_periods,
  build_amount_lookup,
  build_detail_lookup,
  build_trend_chart_rows,
  compute_yoy_deltas,
  order_dimensions_by_deductions,
  period_revenue_map,
  range_from_periods,
  type Dimension,
  type Granularity,
  type Metric,
  type MonthlyDimensionRow,
} from "./yoy_trend"
import { TrendViewState } from "./trend_view_state"

// We always pull a multi-year window so the year-over-year comparison is meaningful,
// independent of the dashboard's (often short) global date filter.
const LOOKBACK_YEARS = 3

const ALL_COLOR = "#0f172a" // slate-900 for the headline "all deductions" line
const FALLBACK_COLOR = "#94a3b8" // slate-400

// 24 visually distinct colors so lines stay unique well past 20 customers.
// getColor() collapses every unknown name to one fallback, so we assign by rank instead.
const LINE_PALETTE = [
  "#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c",
  "#0891b2", "#db2777", "#ca8a04", "#4f46e5", "#0d9488",
  "#7c3aed", "#e11d48", "#65a30d", "#0284c7", "#c026d3",
  "#f59e0b", "#059669", "#be123c", "#1d4ed8", "#a16207",
  "#15803d", "#9f1239", "#6d28d9", "#b45309",
]

/**
 * Builds a name -> color map assigning palette colors by deduction rank, so the
 * first 24 dimension values each get a unique color. ALL_KEY is always slate.
 */
function build_color_map(dimensions_ordered: string[]): Record<string, string> {
  const map: Record<string, string> = { [ALL_KEY]: ALL_COLOR }
  let i = 0
  for (const name of dimensions_ordered) {
    if (name === ALL_KEY) continue
    map[name] = LINE_PALETTE[i % LINE_PALETTE.length]
    i++
  }
  return map
}

/**
 * `label_for` is the server's customer naming (see `useCustomerRefs`), so the legend, the picker
 * and the tooltip read the same as the customer filter and the dashboard export.
 */
function dimension_label(name: string, dimension: Dimension, label_for: (source: string) => string): string {
  if (name === ALL_KEY) return ALL_LABEL
  return dimension === "customer" ? label_for(name) : name
}

function header_index(headers: string[]): Record<string, number> {
  return headers.reduce(
    (acc, header, index) => {
      acc[header] = index
      return acc
    },
    {} as Record<string, number>
  )
}

// DATE_TRUNC returns a midnight UTC timestamp; the rest of the dashboard adds a day
// to correct the off-by-one before formatting. Keep that convention.
function month_key_from_row(raw: string): string {
  return format(addDays(parseISO(raw), 1), "yyyy-MM-dd")
}

interface FetchParams {
  start_date: string
  end_date: string
  distributor: string | null | undefined
  date_kind: string
  granularity: Granularity
}

/**
 * One per-period, per-(source, category) aggregate from the `trend` endpoint. The
 * server buckets per period in its own window so sales/deductions/repayments
 * reconcile exactly with the summary cards for that period (the repayment figure is
 * window-dependent). `month_key` is the first day of the period.
 */
interface TrendApiRow {
  month_key: string
  source: string
  category: string
  sales: number
  deductions: number
  repayments: number
}

async function fetch_trend(params: FetchParams): Promise<TrendApiRow[]> {
  const res = await api_fetch<QueryData>(`/deductions/query`, {
    params: {
      query_name: "trend",
      start_date: params.start_date,
      end_date: params.end_date,
      distributor: params.distributor ?? undefined,
      date_kind: params.date_kind,
      granularity: params.granularity,
    },
  })
  if (!res.ok) throw new Error("Failed to fetch deduction trend")
  const idx = header_index(res.value.data.headers)
  return res.value.data.rows.map((row) => ({
    month_key: month_key_from_row(row[idx.month] as string),
    source: row[idx.source] as string,
    category: row[idx.category] as string,
    sales: Number(row[idx.sales]),
    deductions: Number(row[idx.deductions]),
    repayments: Number(row[idx.repayments]),
  }))
}

function format_metric(value: number | null | undefined, metric: Metric): string {
  if (value == null || Number.isNaN(value)) return "—"
  if (metric === "pct_of_revenue") return `${value.toFixed(1)}%`
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
}

function format_dollars(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—"
  return value.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
}

function format_pct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return "—"
  return `${value.toFixed(1)}%`
}

function format_period(period: string): string {
  // Month keys ("2025-03") render as "Mar 2025"; year/quarter are already friendly.
  if (/^\d{4}-\d{2}$/.test(period)) {
    try {
      return format(parseISO(`${period}-01`), "MMM yyyy")
    } catch {
      return period
    }
  }
  return period
}

interface Drilldown {
  /** Inclusive ISO bounds of the selected range (yyyy-MM-dd). */
  start_iso: string
  end_iso: string
  /** Human label for the selected range, e.g. "2024 – 2025" or "Q2 2025". */
  label: string
}

interface ToggleGroupProps<T extends string> {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
}

function ToggleGroup<T extends string>({ value, options, onChange }: ToggleGroupProps<T>) {
  return (
    <div className="inline-flex rounded-md border border-gray-200 p-0.5">
      {options.map((opt) => (
        <Button
          key={opt.value}
          type="button"
          size="sm"
          variant={value === opt.value ? "default" : "ghost"}
          className="h-7 px-3 text-xs"
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Button>
      ))}
    </div>
  )
}

export function YoyDeductionTrendChart() {
  const [distributor, dateKind] = GlobalFilterState.use((s) => [s.distributor, s.dateKind])
  const { label_for } = useCustomerRefs()
  const [, setLocation] = useLocation()

  const [granularity, set_granularity] = useState<Granularity>("year")
  const [metric, set_metric] = useState<Metric>("pct_of_revenue")
  const [dimension, set_dimension] = useState<Dimension>("customer")
  const [net_repayments, set_net_repayments] = useState(false)
  const [selected, set_selected] = useState<string[]>([ALL_KEY])
  const [search_query, set_search_query] = useState("")

  // Drill-down: a selected time range rendered at monthly granularity.
  const [drilldown, set_drilldown] = useState<Drilldown | null>(null)
  // Drag-to-select state (period keys under the cursor).
  const [ref_left, set_ref_left] = useState<string>("")
  const [ref_right, set_ref_right] = useState<string>("")
  const [selecting, set_selecting] = useState(false)

  // When drilled in, the chart is always monthly; otherwise it follows the toggle.
  const effective_granularity: Granularity = drilldown ? "month" : granularity

  const { end_date, start_date, min_year } = useMemo(() => {
    const end = new Date()
    const min_year = end.getFullYear() - LOOKBACK_YEARS
    const start = new Date(min_year, 0, 1)
    return {
      end_date: format(end, "yyyy-MM-dd"),
      start_date: format(start, "yyyy-MM-dd"),
      min_year,
    }
  }, [])

  // The server buckets per period in its own window (so repayments reconcile with
  // the summary), so we refetch whenever the granularity or drilled range changes.
  // When drilled in we request the drilled range at month granularity; otherwise the
  // full lookback at the selected granularity.
  const fetch_start = drilldown ? drilldown.start_iso : start_date
  const fetch_end = drilldown ? drilldown.end_iso : end_date
  const fetch_params: FetchParams = {
    start_date: fetch_start,
    end_date: fetch_end,
    distributor,
    date_kind: analytics_date_kind(dateKind),
    granularity: effective_granularity,
  }

  const trend_swr = useSWR(
    ["yoy-deduction-trend", fetch_start, fetch_end, distributor ?? "", dateKind, effective_granularity],
    () => fetch_trend(fetch_params),
    { revalidateOnFocus: false }
  )
  const isLoading = trend_swr.isLoading
  const error = trend_swr.error

  // Publish the window and granularity actually on screen so the dashboard export matches it.
  useEffect(() => {
    TrendViewState.set({ granularity: effective_granularity, startDate: fetch_start, endDate: fetch_end })
  }, [effective_granularity, fetch_start, fetch_end])

  // All trend rows within the lookback window (>= min_year).
  const trend_rows = useMemo(
    () => (trend_swr.data ?? []).filter((r) => Number(r.month_key.slice(0, 4)) >= min_year),
    [trend_swr.data, min_year]
  )

  // Deduction rows for the active dimension (source for customers, category for
  // categories); aggregate_to_periods sums duplicates per (period, dimension).
  const monthly_rows = useMemo<MonthlyDimensionRow[]>(
    () =>
      trend_rows.map((r) => ({
        month_key: r.month_key,
        dimension: dimension === "customer" ? r.source : r.category,
        total_sales: dimension === "customer" ? r.sales : 0,
        total_deductions: r.deductions,
      })),
    [trend_rows, dimension]
  )

  // Rows actually charted: the full lookback, or just the drilled range.
  const in_range = (month_key: string) =>
    !drilldown || (month_key >= drilldown.start_iso && month_key <= drilldown.end_iso)

  const view_rows = useMemo(
    () => monthly_rows.filter((r) => in_range(r.month_key)),
    [monthly_rows, drilldown]
  )

  // Revenue per period = total sales across all sources/categories.
  const revenue = useMemo(
    () =>
      period_revenue_map(
        trend_rows
          .filter((r) => in_range(r.month_key))
          .map((r) => ({ month_key: r.month_key, dimension: r.source, total_sales: r.sales, total_deductions: 0 })),
        effective_granularity
      ),
    [trend_rows, effective_granularity, drilldown]
  )

  const dimensions_ordered = useMemo(() => {
    if (!monthly_rows.length) return []
    return [ALL_KEY, ...order_dimensions_by_deductions(monthly_rows)]
  }, [monthly_rows])

  const color_map = useMemo(() => build_color_map(dimensions_ordered), [dimensions_ordered])
  function color_of(name: string): string {
    return color_map[name] ?? FALLBACK_COLOR
  }

  // Reset the selection only when the dimension or the global customer filter
  // changes — NOT on drill-down (which refetches a smaller window), so the user's
  // selected customers persist while drilling.
  useEffect(() => {
    if (dimension === "customer" && distributor) {
      set_selected([distributor])
      return
    }
    set_selected([ALL_KEY])
  }, [dimension, distributor])

  const aggs = useMemo(
    () => aggregate_to_periods(view_rows, effective_granularity),
    [view_rows, effective_granularity]
  )

  // Repayments keyed by the active dimension (source for customers, category for
  // categories), aggregated the same way so they can be netted against deductions.
  const repayment_lookup = useMemo(() => {
    const rows = trend_rows
      .filter((r) => in_range(r.month_key))
      .map((r) => ({
        month_key: r.month_key,
        dimension: dimension === "customer" ? r.source : r.category,
        total_sales: 0,
        total_deductions: r.repayments,
      }))
    return build_amount_lookup(aggregate_to_periods(rows, effective_granularity))
  }, [trend_rows, dimension, effective_granularity, drilldown])

  const build_opts = useMemo(
    () =>
      ({
        metric,
        revenue_basis: dimension === "customer" ? ("per_dimension" as const) : ("period_total" as const),
        period_revenue: revenue,
        repayments: repayment_lookup,
        net_repayments,
      }),
    [metric, dimension, revenue, repayment_lookup, net_repayments]
  )

  const chart_rows = useMemo(
    () => build_trend_chart_rows(aggs, selected, build_opts),
    [aggs, selected, build_opts]
  )

  // [period][dimension] -> { deductions, revenue, pct } so the tooltip can show
  // the dollar figures driving each percentage.
  const detail_lookup = useMemo(() => build_detail_lookup(aggs, build_opts), [aggs, build_opts])

  const yoy_deltas = useMemo(
    () => compute_yoy_deltas(chart_rows, selected, effective_granularity),
    [chart_rows, selected, effective_granularity]
  )

  const selected_set = useMemo(() => new Set(selected), [selected])

  // --- Drag-to-drill selection (recursive: works at any granularity, including
  // the monthly view, where it narrows to specific months) ----------------------
  function handle_mouse_down(e: any) {
    if (!e?.activeLabel) return
    set_selecting(true)
    set_ref_left(String(e.activeLabel))
    set_ref_right(String(e.activeLabel))
  }

  function handle_mouse_move(e: any) {
    if (!selecting || !e?.activeLabel) return
    set_ref_right(String(e.activeLabel))
  }

  function handle_mouse_up() {
    set_selecting(false)
    if (!ref_left || !ref_right) {
      set_ref_left("")
      set_ref_right("")
      return
    }
    // Interpret the selected period keys at the granularity currently on the axis,
    // so dragging in the monthly view narrows to a sub-range of months.
    const [lo, hi] = [ref_left, ref_right].sort((a, b) => a.localeCompare(b))
    const { start, end } = range_from_periods(lo, hi, effective_granularity)
    const label = lo === hi ? format_period(lo) : `${format_period(lo)} – ${format_period(hi)}`
    set_drilldown({ start_iso: start, end_iso: end, label })
    set_ref_left("")
    set_ref_right("")
  }

  // Switching the granularity toggle exits any active drill-down range.
  function change_granularity(g: Granularity) {
    set_granularity(g)
    set_drilldown(null)
  }

  // Open the deductions table scoped to the drilled range + the selected customers.
  function open_deductions() {
    const range = drilldown ?? { start_iso: start_date, end_iso: end_date, label: "" }
    const specific = selected.filter((s) => s !== ALL_KEY)

    GlobalFilterState.set((s) => ({
      ...s,
      startDate: parseISO(range.start_iso),
      endDate: parseISO(range.end_iso),
      // For the customer view, drive the single-customer case through the server
      // distributor filter; multi-customer is handled by a client column filter below.
      distributor: dimension === "customer" ? (specific.length === 1 ? specific[0] : null) : s.distributor,
    }))

    TableState.set((s) => {
      const columnFilters = s.columnFilters.filter((f) => f.id !== "source" && f.id !== "category")
      if (dimension === "customer" && specific.length >= 2) {
        // `source` column = exact-match OR across a newline-delimited token list.
        columnFilters.push({ id: "source", value: specific.join("\n") })
      } else if (dimension === "category" && specific.length >= 1) {
        columnFilters.push({ id: "category", value: specific.join("\n") })
      }
      return { ...s, columnFilters }
    })

    setLocation("/deductions")
  }

  const filtered_list = useMemo(() => {
    const q = search_query.trim().toLowerCase()
    const without_all = dimensions_ordered.filter((d) => d !== ALL_KEY)
    if (!q) return without_all
    // Match the raw source too, so "agne" still finds "Associated Grocers of New England".
    return without_all.filter(
      (name) => dimension_label(name, dimension, label_for).toLowerCase().includes(q) || name.toLowerCase().includes(q)
    )
  }, [dimensions_ordered, search_query, dimension, label_for])

  function toggle(name: string) {
    set_selected((current) =>
      current.includes(name) ? current.filter((x) => x !== name) : [...current, name]
    )
  }

  const comparison_label =
    granularity === "year" ? "year-over-year" : granularity === "quarter" ? "quarter-over-quarter" : "month-over-month"
  const metric_label = metric === "pct_of_revenue" ? "deductions as % of revenue" : "deduction dollars"
  const net_suffix = net_repayments ? " Net of the repayments we've recovered." : ""
  const subtitle = drilldown
    ? `Monthly detail for ${drilldown.label} — ${metric === "pct_of_revenue" ? "% of revenue" : "deduction dollars"}. Drag to narrow to specific months.${net_suffix}`
    : `Comparable ${comparison_label} ${metric_label}, last ${LOOKBACK_YEARS} years. Drag across the chart to drill in.${net_suffix}`

  function header() {
    return (
      <CardHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold">Deduction Trends</h3>
              <p className="text-sm text-gray-500">{subtitle}</p>
            </div>
            {drilldown && (
              <div className="flex shrink-0 flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => set_drilldown(null)}>
                  ← Back
                </Button>
                <Button type="button" size="sm" onClick={open_deductions}>
                  View deductions
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <ToggleGroup<Dimension>
              value={dimension}
              onChange={set_dimension}
              options={[
                { value: "customer", label: "By customer" },
                { value: "category", label: "By category" },
              ]}
            />
            <ToggleGroup<Granularity>
              value={granularity}
              onChange={change_granularity}
              options={[
                { value: "year", label: "Year" },
                { value: "quarter", label: "Quarter" },
                { value: "month", label: "Month" },
              ]}
            />
            <ToggleGroup<Metric>
              value={metric}
              onChange={set_metric}
              options={[
                { value: "pct_of_revenue", label: "% of revenue" },
                { value: "dollars", label: "Dollars" },
              ]}
            />
            <ToggleGroup<"gross" | "net">
              value={net_repayments ? "net" : "gross"}
              onChange={(v) => set_net_repayments(v === "net")}
              options={[
                { value: "gross", label: "Gross" },
                { value: "net", label: "Net of repayments" },
              ]}
            />
          </div>
        </div>
      </CardHeader>
    )
  }

  if (error) {
    return (
      <Card>
        {header()}
        <CardContent>
          <div className="h-40 flex items-center justify-center text-destructive text-sm">
            Could not load deduction trend data.
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        {header()}
        <CardContent>
          <div className="h-48 flex items-center justify-center text-gray-400">Loading deduction trends…</div>
        </CardContent>
      </Card>
    )
  }

  if (!monthly_rows.length) {
    return (
      <Card>
        {header()}
        <CardContent>
          <div className="h-40 flex items-center justify-center text-gray-400">
            No deduction data for this {dimension === "customer" ? "customer" : "category"} view.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      {header()}
      <CardContent>
        {/* YoY delta summary — directly answers "are we improving?" */}
        {yoy_deltas.length > 0 && (
          <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {yoy_deltas.map((d) => {
              const improving = d.delta != null && d.delta < 0 // lower deductions is better
              const delta_label =
                d.delta == null
                  ? "no prior-year data"
                  : `${d.delta > 0 ? "+" : ""}${
                      metric === "pct_of_revenue" ? `${d.delta.toFixed(1)} pts` : format_metric(d.delta, metric)
                    } vs ${format_period(d.prior_period)}`
              return (
                <div key={d.dimension} className="rounded-md border border-gray-100 p-2.5">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color_of(d.dimension) }} />
                    <span className="truncate">{dimension_label(d.dimension, dimension, label_for)}</span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-lg font-semibold">{format_metric(d.current_value, metric)}</span>
                    <span className="text-xs text-gray-400">{format_period(d.current_period)}</span>
                  </div>
                  <div
                    className={`text-xs ${
                      d.delta == null ? "text-gray-400" : improving ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {d.delta != null && (improving ? "▼ " : "▲ ")}
                    {delta_label}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Dimension picker (skip when a single customer is locked via the global filter) */}
        {!(dimension === "customer" && distributor) && (
          <>
            <div className="mb-2">
              <Input
                placeholder={`Search ${dimension === "customer" ? "customers" : "categories"}…`}
                value={search_query}
                onInput={(e: Event) => set_search_query((e.target as HTMLInputElement).value)}
                className="max-w-sm"
              />
            </div>
            <div className="mb-3 max-h-32 overflow-y-auto rounded-md border border-gray-100 p-2 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => toggle(ALL_KEY)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                  selected_set.has(ALL_KEY)
                    ? "border-gray-300 bg-gray-100 text-gray-800"
                    : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                }`}
              >
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color_of(ALL_KEY) }} />
                {ALL_LABEL}
              </button>
              {filtered_list.map((name) => {
                const is_selected = selected_set.has(name)
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => toggle(name)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors ${
                      is_selected
                        ? "border-gray-300 bg-gray-100 text-gray-800"
                        : "border-gray-200 bg-white text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color_of(name) }} />
                    <span className="truncate max-w-[14rem]">{dimension_label(name, dimension, label_for)}</span>
                  </button>
                )
              })}
            </div>
          </>
        )}

        <div className="h-[320px]">
          {!selected.length ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">
              Select at least one {dimension === "customer" ? "customer" : "category"} to show the trend.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chart_rows}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                onMouseDown={handle_mouse_down}
                onMouseMove={handle_mouse_move}
                onMouseUp={handle_mouse_up}
                style={{ cursor: "crosshair" }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="period"
                  tickFormatter={format_period}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(val: number) =>
                    metric === "pct_of_revenue" ? `${val.toFixed(0)}%` : `$${Math.round(val / 1000)}k`
                  }
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11 }}
                  width={50}
                />
                <Tooltip
                  wrapperStyle={{ zIndex: 50 }}
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null
                    const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
                    // For customers the denominator is the customer's own sales; for
                    // categories it's the period's total revenue.
                    const revenue_label = dimension === "customer" ? "Sales" : "Revenue"
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm min-w-[15rem]">
                        <p className="font-semibold mb-2">{format_period(String(label))}</p>
                        {sorted.map((entry: any, idx: number) => {
                          const name = String(entry.name ?? entry.dataKey ?? "")
                          const detail = detail_lookup[String(label)]?.[name]
                          return (
                            <div key={idx} className="mb-2 last:mb-0">
                              <div className="flex items-center gap-1.5 mb-1">
                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
                                <span className="text-gray-700">{dimension_label(name, dimension, label_for)}</span>
                              </div>
                              {detail ? (
                                (() => {
                                  const show_net = net_repayments && detail.repayments > 0
                                  return (
                                    <div className="ml-4 flex items-center gap-3">
                                      {/* Rendered like a math equation: Sales over
                                          (Deductions − Repayments), right-aligned (tabular-nums)
                                          so the digits line up vertically. */}
                                      <div className="grid grid-cols-[auto_auto] items-center gap-x-3">
                                        <span className="text-[10px] uppercase tracking-wide text-gray-400">{revenue_label}</span>
                                        <span className="tabular-nums text-right text-xs text-gray-700">
                                          {format_dollars(detail.revenue)}
                                        </span>
                                        <span className="text-[10px] uppercase tracking-wide text-gray-400 border-t border-gray-400 pt-0.5">
                                          Deductions
                                        </span>
                                        <span className="tabular-nums text-right text-xs text-gray-700 border-t border-gray-400 pt-0.5">
                                          {format_dollars(detail.deductions)}
                                        </span>
                                        {show_net && (
                                          <>
                                            <span className="text-[10px] uppercase tracking-wide text-gray-400">− Repayments</span>
                                            <span className="tabular-nums text-right text-xs text-emerald-600">
                                              −{format_dollars(detail.repayments)}
                                            </span>
                                            <span className="text-[10px] uppercase tracking-wide text-gray-500 border-t border-gray-400 pt-0.5">
                                              Net
                                            </span>
                                            <span className="tabular-nums text-right text-xs font-medium text-gray-900 border-t border-gray-400 pt-0.5">
                                              {format_dollars(detail.effective)}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      <span className="text-base font-semibold text-gray-900">{format_pct(detail.pct)}</span>
                                    </div>
                                  )
                                })()
                              ) : (
                                <div className="ml-4 text-base font-semibold">{format_metric(entry.value, metric)}</div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )
                  }}
                />
                <Legend
                  formatter={(value: string) => dimension_label(value, dimension, label_for)}
                  wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                />
                {selected.map((name) => (
                  <Line
                    key={name}
                    type="monotone"
                    dataKey={name}
                    name={name}
                    stroke={color_of(name)}
                    strokeWidth={name === ALL_KEY ? 3 : 2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
                {ref_left && ref_right && (
                  <ReferenceArea x1={ref_left} x2={ref_right} strokeOpacity={0.3} fill="#6366f1" fillOpacity={0.12} />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
